import React, {
    useEffect,
    useId,
    useRef,
    useMemo,
    useState,
} from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useFormState, useWatch } from 'react-hook-form';
import { useRhCustomContext } from './rh-form-wrapper';
import { CheckCircleIcon, ErrorIcon } from '../../icon';

export interface IRhTab {
    label: string;
    // RHF field names this tab owns. Drives both the error badge (any of these
    // in error) and the "n / x" counter (x = the required subset, n = filled).
    fields: string[];
    // When true, the tab shows no "n / x" progress counter and no completion
    // check. Use it for tabs whose fields are all optional from the user's
    // standpoint even though the schema marks them `required` — e.g. defaulted
    // checkboxes or conditionally-required amounts that can be left as-is. The
    // error badge still appears if any of the tab's fields is invalid.
    optional?: boolean;
    content: React.ReactNode;
    // Optional predicate, evaluated against the live form values, that disables
    // the tab. It must be a function (not a static boolean) because the form
    // component sits outside the RHF provider and never re-renders on field
    // changes — only the wrapper, which watches the values, can react.
    disabled?: (values: Record<string, any>) => boolean;
}

interface IRhTabsWrapper {
    tabs: IRhTab[];
}

// A required field counts as "filled" when it holds a meaningful value.
function isFilled(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
}

// Resolve a dot/bracket field path against the RHF errors object. Returns a
// truthy node if an error exists at (or under) that path.
function getErrorAtPath(errors: Record<string, any>, path: string): unknown {
    return path
        .split(/[.[\]]+/)
        .filter(Boolean)
        .reduce<any>(
            (node, key) => (node == null ? node : node[key]),
            errors,
        );
}

function TabLabel({ tab, disabled }: { tab: IRhTab; disabled: boolean }) {
    const { isFieldRequired } = useRhCustomContext();
    const { errors } = useFormState();

    // A disabled tab is "not applicable", so it never advertises errors or a
    // progress count — its label stays plain.
    const hasError =
        !disabled &&
        tab.fields.some((name) => Boolean(getErrorAtPath(errors, name)));

    // An `optional` tab opts out of the progress count entirely (no required
    // subset), so it never advertises a counter or a completion check.
    const requiredFields = useMemo(
        () =>
            tab.optional
                ? []
                : tab.fields.filter((name) => isFieldRequired(name)),
        [tab.optional, tab.fields, isFieldRequired],
    );

    // useWatch with an empty name list would subscribe to the whole form, so
    // guard it and only watch the required fields we actually need to count.
    const watched = useWatch({
        name: requiredFields.length ? requiredFields : ['__rh_tabs_noop__'],
    });
    const filledCount = requiredFields.length
        ? (watched as unknown[]).filter(isFilled).length
        : 0;

    const showCounter = !disabled && requiredFields.length > 0;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textTransform: 'none',
            }}
        >
            <Typography variant={'button'}>{tab.label}</Typography>
            {hasError ? (
                <ErrorIcon fontSize={'small'} color={'error'} />
            ) : showCounter && filledCount === requiredFields.length ? (
                <CheckCircleIcon fontSize={'small'} color={'success'} />
            ) : null}
            {showCounter ? (
                <Typography
                    variant={'caption'}
                    color={hasError ? 'error.main' : 'text.secondary'}
                >
                    {filledCount} / {requiredFields.length}
                </Typography>
            ) : null}
        </Box>
    );
}

export function RhTabsWrapper({ tabs }: IRhTabsWrapper) {
    const [active, setActive] = useState(0);
    const wrapperId = useId();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<Array<HTMLElement | null>>([]);
    const initialFocusDone = useRef(false);

    // Give a newly visible, non-modal tabbed form a deterministic starting
    // focus. DModal repeats this after its transition for modal forms (where
    // the wrapper may be mounted while hidden), so Alt+ArrowUp is usable from
    // the first render instead of requiring an extra click into the form.
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (
            initialFocusDone.current ||
            !wrapper ||
            wrapper.getClientRects().length === 0
        ) {
            return;
        }
        const frame = window.setTimeout(() => {
            if (!wrapper.contains(document.activeElement)) {
                tabRefs.current[active]?.focus();
            }
            initialFocusDone.current = true;
        });
        return () => window.clearTimeout(frame);
    }, [active]);

    // Watch every value so disabled predicates re-evaluate as the user edits.
    const values = useWatch() as Record<string, any>;
    const disabledStates = tabs.map((tab) =>
        tab.disabled ? tab.disabled(values) : false,
    );

    // If the active tab becomes disabled (e.g. the user unticked "Cliente"
    // while on the Cliente tab), fall back to the first enabled tab.
    useEffect(() => {
        if (disabledStates[active]) {
            const next = disabledStates.findIndex((d) => !d);
            if (next !== -1) {
                const tabHadFocus =
                    typeof document !== 'undefined' &&
                    document.activeElement === tabRefs.current[active];
                setActive(next);
                if (tabHadFocus) {
                    tabRefs.current[next]?.focus();
                }
            }
        }
    }, [disabledStates, active]);

    // Listen at the document boundary so the shortcut also works when focus is
    // on the dialog header/actions or has fallen back to the page body. Scope it
    // to this wrapper's visible dialog/form so another open surface cannot steal
    // the shortcut. Plain arrows remain entirely owned by the focused control.
    useEffect(() => {
        const handleTabFocusShortcut = (event: KeyboardEvent) => {
            if (
                event.key !== 'ArrowUp' ||
                !event.altKey ||
                event.shiftKey ||
                event.ctrlKey ||
                event.metaKey
            ) {
                return;
            }

            const wrapper = wrapperRef.current;
            if (!wrapper || wrapper.getClientRects().length === 0) return;

            const target =
                event.target instanceof HTMLElement ? event.target : null;
            const activeElement =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;
            const focusIsNeutral =
                activeElement === null || activeElement === document.body;
            const scope =
                (wrapper.closest('[role="dialog"]') as HTMLElement | null) ||
                (wrapper.closest('form') as HTMLElement | null) ||
                wrapper;

            if (!focusIsNeutral && (!target || !scope.contains(target))) {
                return;
            }

            // If focus is neutral, only the last visible tab wrapper responds.
            // Dialog portals are appended last, so an open dialog wins over any
            // tabbed form that may still be mounted behind it.
            if (focusIsNeutral) {
                const visibleWrappers = Array.from(
                    document.querySelectorAll<HTMLElement>(
                        '[data-rh-tabs-wrapper="true"]',
                    ),
                ).filter((element) => element.getClientRects().length > 0);
                if (visibleWrappers[visibleWrappers.length - 1] !== wrapper) {
                    return;
                }
            }

            const autocompleteIsOpen = Boolean(
                target?.closest?.('[role="combobox"][aria-expanded="true"]'),
            );
            // Alt+ArrowUp is a native close gesture for selects and open
            // autocompletes; let those controls keep it.
            if (target?.closest?.('select') || autocompleteIsOpen) return;

            event.preventDefault();
            event.stopPropagation();
            tabRefs.current[active]?.focus();
        };

        document.addEventListener('keydown', handleTabFocusShortcut, true);
        return () =>
            document.removeEventListener(
                'keydown',
                handleTabFocusShortcut,
                true,
            );
    }, [active]);

    return (
        <Box
            ref={wrapperRef}
            data-rh-tabs-wrapper={'true'}
            sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={active}
                    onChange={(_, value) => setActive(value)}
                    variant={'scrollable'}
                    scrollButtons={'auto'}
                    selectionFollowsFocus
                >
                    {tabs.map((tab, index) => (
                        <Tab
                            key={index}
                            id={`${wrapperId}-tab-${index}`}
                            aria-controls={`${wrapperId}-tabpanel-${index}`}
                            ref={(element) => {
                                tabRefs.current[index] = element;
                            }}
                            disabled={disabledStates[index]}
                            label={
                                <TabLabel
                                    tab={tab}
                                    disabled={disabledStates[index]}
                                />
                            }
                        />
                    ))}
                </Tabs>
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    sx={{ display: 'block', px: 2, pb: 0.5 }}
                >
                    Alt+↑ para enfocar pestañas; luego ←/→ para cambiar
                </Typography>
            </Box>

            {/* Enabled panels stay mounted so hidden tabs keep validating; only
                the active one is visible. Disabled tabs are unmounted so their
                (not-applicable) fields don't register or block submission. */}
            {tabs.map((tab, index) =>
                disabledStates[index] ? null : (
                    <Box
                        key={index}
                        role={'tabpanel'}
                        id={`${wrapperId}-tabpanel-${index}`}
                        aria-labelledby={`${wrapperId}-tab-${index}`}
                        hidden={index !== active}
                        sx={{
                            display: index === active ? 'block' : 'none',
                            px: 2,
                            py: 2,
                        }}
                    >
                        {tab.content}
                    </Box>
                ),
            )}
        </Box>
    );
}
